import {
  AnyID,
  Getter,
  Item,
  ItemGroup,
  LinkedItem,
  Partition,
  SceneObjects,
  Setter,
  Store,
  Types,
  Walls,
} from '@types'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import decomposeMatrix from '@utils/decomposeMatrix'
import isWall from '@utils/sceneObjects/assertions/isWall'
import {
  applyDeltaToSnapshot,
  applyIncrementalGroupTransform,
  captureSelectionTransformSnapshot,
  isTransformableSelectionItem,
  type ApplySelectionTransformOptions,
  type TransformableSelectionItem,
} from '@utils/sceneObjects/selectionGroupTransform'
import { purgeSceneGlbForCatalog } from '@utils/sceneObjects/purgeSceneGlbForCatalog'
import { removeLinkedHierarchyFromStore } from '@utils/sceneObjects/removeLinkedHierarchyFromStore'
import { releaseSnappedSides } from '@utils/sceneObjects/snappedSides'
import { getWallCOBB } from '@utils/sceneObjects/walls/getWallCOBB'
import { Camera, Matrix3, Matrix4, Vector2, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

function getMaxWallHeight(
  item: TransformableSelectionItem,
  items: Map<string, SceneObjects>,
): number | null {
  if ('snappedWall' in item && item.snappedWall) {
    const wall = items.get(item.snappedWall) as Walls
    if (isWall(wall)) return wall.height
  }

  let maxHeight = 0
  items.forEach((sceneObject) => {
    if (isWall(sceneObject) && sceneObject.height > maxHeight) {
      maxHeight = sceneObject.height
    }
  })
  return maxHeight > 0 ? maxHeight : null
}

function restrictItemY(
  y: number,
  item: TransformableSelectionItem,
  mode: string,
  items: Map<string, SceneObjects>,
): number {
  const is3DOrAlzados =
    mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS'

  if (!is3DOrAlzados) return y

  let restrictedY = Math.max(0, y)

  if ('height' in item && item.height) {
    const maxWallHeight = getMaxWallHeight(item, items)
    if (maxWallHeight !== null) {
      restrictedY = Math.min(restrictedY, maxWallHeight - item.height)
    }
  }

  return restrictedY
}

function buildItemFromMatrix(
  item: TransformableSelectionItem,
  matrix: Matrix4,
  restrictedY: number,
): TransformableSelectionItem {
  const { position, rotation } = decomposeMatrix(matrix)
  const newMatrix = new Matrix4()
    .makeRotationFromQuaternion(rotation)
    .setPosition(position.x, restrictedY, position.z)

  return {
    ...item,
    matrix: newMatrix,
    x: position.x,
    y: restrictedY,
    z: position.z,
  }
}
export type SceneActions = ReturnType<typeof createSceneActions>

export const createSceneActions = (set: Setter, get: Getter) => ({
  select: (id: AnyID, isCtrlPressed: boolean) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<string>
      const newSelectedItems = new Set(selectedItems)

      // Si no hay control presionado, limpiar la selección en ciertos casos
      if (
        (!isCtrlPressed && selectedItems.size < 2) ||
        (!isCtrlPressed && !selectedItems.has(id))
      ) {
        newSelectedItems.clear()
      }

      // Si hay control presionado y el item ya está seleccionado, quitarlo
      if (isCtrlPressed && selectedItems.has(id)) {
        newSelectedItems.delete(id)
        store.currentObjectID = ''
      } else {
        // Agregar el item a la selección
        newSelectedItems.add(id)
        store.currentObjectID = id
      }

      // Asignar el nuevo Set para triggerar reactividad
      store.layers[store.currentLayer].selected = newSelectedItems
    })
  },
  unselect: (id: AnyID) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<string>
      const newSelectedItems = new Set(selectedItems)
      newSelectedItems.delete(id)
      store.layers[store.currentLayer].selected = newSelectedItems
      store.currentObjectID === id ? (store.currentObjectID = '') : ''
    })
  },

  unselectAll: () => {
    set((store) => {
      store.layers[store.currentLayer].selected = new Set()
      store.layers[store.currentLayer].selectionTransform = null
      store.currentObjectID = ''
    })
  },
  // TODO: esto debería no existir: Es parte de multiselección
  deleteSelectedObjects: () => {
    set((store: Store) => {
      const selected = store.layers[store.currentLayer].selected as Set<string>
      if (selected) {
        selected.forEach((el: string) => {
          if (!el) return

          const currItem = store.items.get(el) as SceneObjects
          if (!currItem) return

          // Si es un itemGroup, borrar el grupo y todos sus hijos
          if (currItem.type === 'itemGroup') {
            const group = currItem as ItemGroup
            const childrenIds = Array.from(group.children) as AnyID[]

            // Borrar cada hijo del grupo
            childrenIds.forEach((childId) => {
              const child = store.items.get(childId)

              if (child && child.type === 'linkedItem') {
                removeLinkedHierarchyFromStore(store, childId)
              }

              // Borrar el hijo
              if (child) {
                const childCatalogID = child.catalogID
                const childCodigo = (child as { codigo?: string }).codigo
                store.price -= child.price || 0
                releaseSnappedSides(store.items, childId)
                store.items.delete(childId)
                store.sceneBoundingBoxes.delete(childId)
                store.collisionableItems.delete(childId)
                store.layers[store.currentLayer].items.delete(childId)
                purgeSceneGlbForCatalog(store, childCatalogID, childCodigo)
              }
            })

            // Borrar el grupo mismo
            store.price -= group.price || 0
            store.items.delete(el)
            store.sceneBoundingBoxes.delete(el)
            store.collisionableItems.delete(el)
            store.layers[store.currentLayer].items.delete(el)
            return
          }

          if (currItem.type === 'linkedItem') {
            removeLinkedHierarchyFromStore(store, el)
          }

          if (currItem.snappedWall) {
            const wall = store.items.get(currItem.snappedWall) as Walls
            if (wall) {
              if (currItem.tipoNormalizado === 'MAL') {
                wall.highItems.delete(el)
              }
              if (currItem.tipoNormalizado === 'MBJ' || currItem.tipoNormalizado === 'COL') {
                wall.lowItems.delete(el)
              }
            }
          }

          store.price -= currItem.price || 0

          const deletedCatalogID = currItem.catalogID
          const deletedCodigo = (currItem as { codigo?: string }).codigo
          releaseSnappedSides(store.items, el as AnyID)
          store.items.delete(el)
          store.sceneBoundingBoxes.delete(el)
          store.collisionableItems.delete(el)
          store.sceneBoundingBoxes.delete(getWallCOBB(el))
          store.collisionableItems.delete(getWallCOBB(el))
          store.layers[store.currentLayer].items.delete(el)
          purgeSceneGlbForCatalog(store, deletedCatalogID, deletedCodigo)
        })
        selected.clear()
      }
      store.currentObjectID = ''
    })
  },

  setCurrentObjectID: (id: AnyID | '') => {
    set((store) => {
      if (!id && id !== '') return

      store.currentObjectID = id
    })
  },
  //TO-DO:esto no se esta usando no hace falta pa nada
  addBoundingBox: (id: string, boundingObject: { type: Types; boundingBox: OBB }) => {
    set((store) => {
      store.sceneBoundingBoxes.set(id, boundingObject)
    })
  },
  setDeltas: (
    deltas: Map<
      string,
      Array<{ targetId: string; deltaX: number; deltaY: number; deltaZ: number }>
    >,
  ) => {
    set((store) => {
      deltas.forEach((value, key) => {
        store.layers[store.currentLayer].deltas.set(key, value)
      })
    })
  },
  notSameItems: () => {
    const store = get()
    const selectedItems = new Set(
      Array.from(store.layers[store.currentLayer].selected).filter((id) => store.items.has(id)),
    )

    // Mostrar sidebar de multiselección cuando hay 2 o más items seleccionados
    // (ya sean iguales o diferentes)
    if (selectedItems.size < 2) return false

    // Verificar que todos los items seleccionados sean items o linkedItems válidos
    for (const el of selectedItems) {
      const currItem = store.items.get(el) as SceneObjects

      if (!currItem) {
        // console.warn(`⚠️ El objeto con ID ${el} no se encontró en store.items`)
        continue
      }

      // Validar que sea un tipo de objeto editable (items, linkedItems, partitions)
      if (!['items', 'linkedItem', 'partition'].includes(currItem.type)) {
        return false
      }
    }

    return true
  },

  beginSelectionTransform: (pivotId: AnyID) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
      const layer = store.layers[store.currentLayer]

      if (selectedItems.size < 2) {
        layer.selectionTransform = null
        return
      }

      layer.selectionTransform = captureSelectionTransformSnapshot(
        selectedItems,
        store.items,
        pivotId,
      )
    })
  },

  endSelectionTransform: () => {
    const state = get()
    const selectedItems = state.layers[state.currentLayer].selected as Set<AnyID>

    set((store) => {
      store.layers[store.currentLayer].selectionTransform = null
    })

    if (selectedItems.size < 2) return

    selectedItems.forEach((id) => {
      const item = state.items.get(id)
      if (isTransformableSelectionItem(item)) {
        get().setOBB(id)
      }
    })
  },

  applySelectionTransform: (
    pivotId: AnyID,
    currentPivotMatrix: Matrix4,
    options?: ApplySelectionTransformOptions,
  ) => {
    set((store) => {
      const layer = store.layers[store.currentLayer]
      const selectedItems = layer.selected as Set<AnyID>
      if (selectedItems.size < 2) return

      const snapshot = layer.selectionTransform
      let matrices: Map<AnyID, Matrix4> | null = null

      if (snapshot?.pivotId === pivotId) {
        matrices = applyDeltaToSnapshot(snapshot, currentPivotMatrix)
      } else if (options?.previousPivotMatrix) {
        matrices = applyIncrementalGroupTransform(
          selectedItems,
          store.items,
          pivotId,
          options.previousPivotMatrix,
          currentPivotMatrix,
        )
      }

      if (!matrices) return

      const mode = store.mode

      matrices.forEach((matrix, id) => {
        const item = store.items.get(id)
        if (!isTransformableSelectionItem(item)) return

        const { position } = decomposeMatrix(matrix)
        const restrictedY = restrictItemY(position.y, item, mode, store.items)
        store.items.set(id, buildItemFromMatrix(item, matrix, restrictedY))
      })
    })
  },

  updateSelectedObjectsPosition: (currentObjectId: AnyID, matrix: Matrix4) => {
    get().applySelectionTransform(currentObjectId, matrix)
  },

  updateSelectedObjectsTraslation: (
    currentObjectId: AnyID,
    matrix: Matrix4,
    _prevRot: number,
  ) => {
    get().applySelectionTransform(currentObjectId, matrix)
  },

  updateSelectedObjectsPosition2D: (currentObjectId: AnyID, matrix: Matrix4) => {
    get().applySelectionTransform(currentObjectId, matrix)
  },

  updateSelectedObjectsTraslation2D: (
    currentObjectId: AnyID,
    matrix: Matrix4,
    _prevRot: number,
  ) => {
    get().applySelectionTransform(currentObjectId, matrix)
  },

  projectItemsOnWall: (itemID: AnyID, wallID: AnyID, matrix: Matrix4, prevRot: number) => {
    set((store) => {
      const items = store.items
      const selectedItems = store.layers[store.currentLayer].selected as Set<string>
      const deltas = store.layers[store.currentLayer].deltas as Map<
        string,
        { deltaX: number; deltaY: number; deltaZ: number }[]
      >
      const item = items.get(itemID) as Item
      const wall = items.get(wallID) as Walls

      if (!item || !wall) return

      // Proyectar el item principal en la pared
      const itemMorphs = item.morphs.find((el) => el.name === 'Ancho')
      const itemWidth = itemMorphs?._default ?? itemMorphs?.default ?? 0

      const { rotation, width } = wall

      const itemPosition = toVector3()
      matrix.decompose(itemPosition, toQuaternion(), toVector3())
      const end = toVector3(wall.end)
      const start = toVector3(wall.start)
      const wallDirection = end.clone().sub(start)
      const itemToStart = itemPosition.clone().sub(start)
      const projectedVector = itemToStart.projectOnVector(wallDirection.clone().normalize())

      if (Math.abs(projectedVector.x) === 0) {
        projectedVector.x = 0
      }
      if (Math.abs(projectedVector.z) === 0) {
        projectedVector.z = 0
      }
      if (Math.abs(projectedVector.x) > width - itemWidth) {
        if (wallDirection.x < 0) {
          projectedVector.x = itemWidth - width
        } else {
          projectedVector.x = -itemWidth + width
        }
      }
      if (Math.abs(projectedVector.x) < 0) {
        projectedVector.x = 0
      }
      if (Math.abs(projectedVector.z) > width - itemWidth) {
        if (wallDirection.z < 0) {
          projectedVector.z = itemWidth - width
        } else {
          projectedVector.z = -itemWidth + width
        }
      }
      if (Math.abs(projectedVector.z) < 0) {
        projectedVector.z = -itemWidth
      }

      let length = projectedVector.length()
      const wallLength = wallDirection.length()
      let alongWall = projectedVector.length()

      if (projectedVector.dot(wallDirection.normalize()) < 0) {
        alongWall = 0
      } else if (alongWall > wallLength) {
        alongWall = wallLength - itemWidth
      }

      const adjustedPosition = start
        .clone()
        .add(wallDirection.normalize().multiplyScalar(alongWall))

      if (length < 0) {
        length = 0
      }
      if (length > width || length + item.width > width) {
        length = width
      }

      if (!item) return

      // Aplicar restricción de altura en modo 3D o alzados
      let restrictedY = itemPosition.y
      const mode = store.mode
      if (mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS') {
        // Restringir mínimo a 0 siempre
        restrictedY = Math.max(0, restrictedY)

        // Si el item tiene altura, aplicar restricción máxima
        if ('height' in item && item.height) {
          // Usar la altura de la pared a la que está pegado
          const maxWallHeight = wall.height

          // Restringir máximo considerando la altura del mueble
          const maxYAllowed = maxWallHeight - item.height
          restrictedY = Math.min(restrictedY, maxYAllowed)
        }
      }

      item.matrix.makeRotationFromEuler(new Euler(0, -rotation, 0, 'XYZ'))

      if (!item.visible) item.visible = true
      item.matrix.setPosition(adjustedPosition.x, restrictedY, adjustedPosition.z)
      item.x = adjustedPosition.x
      item.z = adjustedPosition.z
      item.y = restrictedY
      // ItemGroups no deben tener snappedWall ni agregarse a las paredes
      if (item.type !== 'itemGroup') {
        item.snappedWall = wallID
      }
      if (item.tipoNormalizado === 'MAL') {
        wall.highItems.add(itemID)
      }
      if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
        wall.lowItems.add(itemID)
      }

      items.set(itemID, item)
      selectedItems.forEach((id) => {
        if (id === itemID) return // Saltar el elemento que se está rotando
        const selectedItem = items.get(id) as Item
        if (!selectedItem) return
        const itemDeltas = deltas.get(itemID)
        if (!itemDeltas) return
        const delta = itemDeltas.find(
          (d) =>
            d.deltaX === selectedItem.x - item.x &&
            d.deltaY === selectedItem.y - item.y &&
            d.deltaZ === selectedItem.z - item.z,
        )
        if (!delta) return
        const deltaVector = toVector3([delta.deltaX, delta.deltaY, delta.deltaZ])
        // Proyectar el delta en la dirección del projectedVector
        const deltaProjected = deltaVector.projectOnVector(projectedVector)
        // Calcular la nueva posición paralela al projectedVector
        const newPosition = adjustedPosition.clone().add(deltaProjected)

        selectedItem.matrix.setPosition(newPosition.x, newPosition.y, newPosition.z)
        selectedItem.x = newPosition.x
        selectedItem.y = newPosition.y
        selectedItem.z = newPosition.z

        items.set(id, selectedItem)
      })
    })
  },
  getFavorites: () => {
    const store = get()
    const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
    const items = store.items
    const deltas = store.layers[store.currentLayer].deltas as Map<
      AnyID,
      { deltaX: number; deltaY: number; deltaZ: number }[]
    >

    const favorites = {
      items: new Map() as Map<AnyID, Item | LinkedItem>,
      deltas: new Map<AnyID, { deltaX: number; deltaY: number; deltaZ: number }[]>(),
    }

    selectedItems.forEach((id) => {
      const item = items.get(id) as Item | LinkedItem
      if (item) {
        favorites.items.set(id, item)
      }
    })

    if (selectedItems.size > 1) {
      favorites.deltas = deltas
    }

    return favorites
  },

  // Drag Selection Rectangle Actions
  setDragSelectionScreenStart: (point: [number, number] | null) => {
    set((store) => {
      store.dragSelectionScreenStart = point
    })
  },

  setDragSelectionScreenEnd: (point: [number, number] | null) => {
    set((store) => {
      store.dragSelectionScreenEnd = point
    })
  },

  setIsDragSelecting: (value: boolean) => {
    set((store) => {
      store.isDragSelecting = value
    })
  },

  selectObjectsInRectangle: (startWorld: [number, number], endWorld: [number, number]) => {
    set((store) => {
      // Crear un nuevo Set vacío para la selección
      const newSelectedItems = new Set<string>()

      // Calcular las coordenadas del rectángulo (min/max) en coordenadas del mundo
      const minX = Math.min(startWorld[0], endWorld[0])
      const maxX = Math.max(startWorld[0], endWorld[0])
      const minZ = Math.min(startWorld[1], endWorld[1])
      const maxZ = Math.max(startWorld[1], endWorld[1])

      // console.log('🔍 Rectángulo de selección:', { minX, maxX, minZ, maxZ })

      // Recorrer todos los items y verificar si están dentro del rectángulo
      store.items.forEach((item, id) => {
        if (!item) return

        // Solo permitir seleccionar: itemGroup, items, linkedItem
        const isSelectableType =
          item.type === 'itemGroup' || item.type === 'items' || item.type === 'linkedItem'
        if (!isSelectableType) return

        // Excluir items invisibles
        if ('visible' in item && !item.visible) return

        // Obtener dimensiones y matriz
        if (!('matrix' in item) || !item.matrix) return
        if (!('width' in item) || !('thickness' in item)) return

        const itemWidth = item.width as number
        const itemThickness = item.thickness as number

        // Descomponer la matriz para obtener posición (esquina trasera inferior izquierda) y rotación
        const cornerPosition = toVector3()
        const quaternion = toQuaternion()
        const scale = toVector3()
        ;(item.matrix as Matrix4).decompose(cornerPosition, quaternion, scale)

        // console.log(`📦 Item ${id}:`, {
        //   cornerPos: { x: cornerPosition.x, z: cornerPosition.z },
        //   dim: { width: itemWidth, thickness: itemThickness },
        // })

        // Calcular las 4 esquinas del item en coordenadas locales
        // La posición de la matriz es la esquina trasera inferior izquierda (origen local)
        // Las esquinas se extienden desde ese origen en las direcciones +X (width) y +Z (thickness)
        const localCorners = [
          new Vector3(0, 0, 0), // Esquina trasera izquierda (origen)
          new Vector3(itemWidth, 0, 0), // Esquina trasera derecha
          new Vector3(0, 0, itemThickness), // Esquina frontal izquierda
          new Vector3(itemWidth, 0, itemThickness), // Esquina frontal derecha
        ]

        // Rotar cada esquina desde el origen y luego trasladar a la posición del item
        const worldCorners = localCorners.map((corner) => {
          const worldCorner = corner.clone()
          worldCorner.applyQuaternion(quaternion)
          worldCorner.add(cornerPosition)
          return {
            x: worldCorner.x,
            z: worldCorner.z,
          }
        })

        // console.log('  Esquinas en mundo:', worldCorners)

        // Calcular cuántas esquinas están dentro del rectángulo
        const cornersInBounds = worldCorners.filter(
          (corner) => corner.x >= minX && corner.x <= maxX && corner.z >= minZ && corner.z <= maxZ,
        ).length

        // console.log(`  ✓ Esquinas dentro: ${cornersInBounds}/4`)

        // Seleccionar si TODAS las 4 esquinas están dentro
        if (cornersInBounds === 4) {
          newSelectedItems.add(id)
        }
      })

      // console.log('✅ Items seleccionados:', newSelectedItems.size)

      // Reemplazar el Set completo (dispara reactividad en Zustand)
      store.layers[store.currentLayer].selected = newSelectedItems

      // Actualizar currentObjectID con el último seleccionado si hay alguno
      if (newSelectedItems.size > 0) {
        const lastSelected = Array.from(newSelectedItems)[newSelectedItems.size - 1]
        store.currentObjectID = lastSelected as AnyID
      } else {
        store.currentObjectID = ''
      }
    })
  },

  selectObjectsInFrustum3D: (startNDC: Vector2, endNDC: Vector2, camera: Camera) => {
    set((store) => {
      // Crear un nuevo Set vacío para la selección
      const newSelectedItems = new Set<string>()

      // Calcular las coordenadas del rectángulo de selección en NDC (min/max)
      const minX = Math.min(startNDC.x, endNDC.x)
      const maxX = Math.max(startNDC.x, endNDC.x)
      const minY = Math.min(startNDC.y, endNDC.y)
      const maxY = Math.max(startNDC.y, endNDC.y)

      // Método de proyección: proyectar los vértices de cada OBB a pantalla
      // y verificar si alguno cae dentro del rectángulo de selección
      const tempVec = new Vector3()

      // Recorrer todos los items
      store.items.forEach((item, id) => {
        if (!item) return

        // Solo permitir seleccionar: itemGroup, items, linkedItem
        const isSelectableType =
          item.type === 'itemGroup' || item.type === 'items' || item.type === 'linkedItem'
        if (!isSelectableType) return

        // Excluir items invisibles
        if ('visible' in item && !item.visible) return

        // Obtener la OBB del item desde sceneBoundingBoxes
        const obbData = store.sceneBoundingBoxes.get(id)
        if (!obbData) return
        const obb = obbData.boundingBox

        // Obtener el centro y tamaños de la OBB
        const obbCenter = obb.center
        const halfSize = obb.halfSize
        const rotation = obb.rotation

        // Generar los 8 vértices de la OBB en espacio mundo
        const vertices: Vector3[] = []
        for (let i = 0; i < 8; i++) {
          const x = (i & 1) === 0 ? -halfSize.x : halfSize.x
          const y = (i & 2) === 0 ? -halfSize.y : halfSize.y
          const z = (i & 4) === 0 ? -halfSize.z : halfSize.z

          const vertex = new Vector3(x, y, z)
          vertex.applyMatrix3(rotation)
          vertex.add(obbCenter)
          vertices.push(vertex)
        }

        // Proyectar cada vértice a coordenadas de pantalla (NDC)
        // y verificar si TODAS las 8 esquinas están dentro del rectángulo de selección
        let allVerticesInside = true

        for (const vertex of vertices) {
          // Clonar para no modificar el original
          tempVec.copy(vertex)

          // Proyectar a NDC
          tempVec.project(camera)

          // Verificar si el vértice proyectado NO está dentro del rectángulo de selección
          // o está detrás de la cámara (z >= 1)
          if (
            tempVec.x < minX ||
            tempVec.x > maxX ||
            tempVec.y < minY ||
            tempVec.y > maxY ||
            tempVec.z >= 1 // Verificar que NO esté detrás de la cámara
          ) {
            allVerticesInside = false
            break
          }
        }

        // Seleccionar el item solo si TODAS las 8 esquinas están dentro del frustum
        if (allVerticesInside) {
          newSelectedItems.add(id)
        }
      })

      // Reemplazar el Set completo (dispara reactividad en Zustand)
      store.layers[store.currentLayer].selected = newSelectedItems

      // Actualizar currentObjectID con el último seleccionado si hay alguno
      if (newSelectedItems.size > 0) {
        const lastSelected = Array.from(newSelectedItems)[newSelectedItems.size - 1]
        store.currentObjectID = lastSelected as AnyID
      } else {
        store.currentObjectID = ''
      }
    })
  },
})
